import { useState, useEffect } from 'react';
import { DollarSign, Database } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WalletsTab({ wallets, activeSubTab, setToast }) {
  const [walletHistory, setWalletHistory] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showWalletHistoryModal, setShowWalletHistoryModal] = useState(false);

  const loadWalletHistory = async (wallet) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/super-admin/wallets/${wallet.wallet_id}/history`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedWallet(wallet);
      setWalletHistory(response.data);
      setShowWalletHistoryModal(true);
    } catch (error) {
      setToast({ type: 'error', message: 'Error al cargar historial de wallet' });
    }
  };

  const commissionWallets = wallets.filter(w => w.wallet_type === 'commission' || w.wallet_name.toLowerCase().includes('comision'));
  const mainWallets = wallets.filter(w => w.wallet_type !== 'commission');

  if (activeSubTab === 'commission-wallets') {
    return (
      <div data-testid="commission-wallets-tab">
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#051C2C' }}>Wallets de Comisiones por Comercio</h2>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-orange-900 font-medium">Vista de comisiones generadas por cada comercio</p>
          <p className="text-sm text-orange-800 mt-1">Estas wallets acumulan las comisiones de cada transaccion y son dispersadas segun la configuracion del sistema.</p>
        </div>

        <CommissionLiquidationPanel setToast={setToast} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {commissionWallets.map((wallet) => (
            <div key={wallet.wallet_id} className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6 border-2 border-orange-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => loadWalletHistory(wallet)} title="Click para ver historial">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2"><DollarSign className="w-5 h-5 text-orange-600" /><h3 className="font-semibold text-lg" style={{ color: '#051C2C' }}>Comisiones</h3></div>
                  <p className="text-sm text-gray-600">Proyecto: {wallet.project_name || 'N/A'}</p>
                  <p className="text-xs text-gray-500 mt-1">Merchant ID: {wallet.merchant_id?.substring(0, 8)}...</p>
                </div>
                <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full font-medium">COMISION</span>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Balance Actual</p>
                  <p className="text-3xl font-bold text-orange-600">{wallet.currency} {wallet.balance.toFixed(2)}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-orange-200">
                  <div><p className="text-xs text-gray-600">Total Recibido</p><p className="text-sm font-semibold text-green-600">+{wallet.currency} {wallet.total_received.toFixed(2)}</p></div>
                  <div><p className="text-xs text-gray-600">Total Enviado</p><p className="text-sm font-semibold text-red-600">-{wallet.currency} {wallet.total_sent.toFixed(2)}</p></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {commissionWallets.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border">
            <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No hay wallets de comisiones creadas</p>
          </div>
        )}
        <WalletHistoryModal show={showWalletHistoryModal} wallet={selectedWallet} history={walletHistory} onClose={() => { setShowWalletHistoryModal(false); setSelectedWallet(null); setWalletHistory([]); }} />
      </div>
    );
  }

  return (
    <div data-testid="wallets-tab">
      <h2 className="text-2xl font-bold mb-6">Wallets Principales del Sistema</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {mainWallets.map((wallet) => (
          <div key={wallet.wallet_id} className="bg-white rounded-xl p-6 border">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold">{wallet.wallet_name}</h3>
                <p className="text-xs text-gray-500">{wallet.wallet_type}</p>
                <p className="text-xs text-gray-500">Proyecto: {wallet.project_name || 'N/A'}</p>
              </div>
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">{wallet.status}</span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Balance</p>
              <p className="text-2xl font-bold" style={{ color: '#003594' }}>{wallet.currency} {wallet.balance.toFixed(2)}</p>
            </div>
          </div>
        ))}
      </div>
      <WalletHistoryModal show={showWalletHistoryModal} wallet={selectedWallet} history={walletHistory} onClose={() => { setShowWalletHistoryModal(false); setSelectedWallet(null); setWalletHistory([]); }} />
    </div>
  );
}

function WalletHistoryModal({ show, wallet, history, onClose }) {
  if (!show || !wallet) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>Historial de Transacciones</h2>
          <p className="text-sm text-gray-600 mt-1">Wallet: {wallet.wallet_name}</p>
        </div>
        <div className="p-6 max-h-[500px] overflow-y-auto">
          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-500"><p>No hay transacciones registradas</p></div>
          ) : (
            <div className="space-y-2">
              {history.map((tx) => (
                <div key={tx.transaction_id} className={`border-l-4 rounded-lg p-4 ${tx.type.includes('in') || tx.type.includes('credit') ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className={`text-2xl font-bold ${tx.type.includes('in') || tx.type.includes('credit') ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.type.includes('in') || tx.type.includes('credit') ? '+' : '-'}VES {Math.abs(tx.amount).toFixed(2)}
                      </span>
                      <p className="text-sm text-gray-700 font-medium">{tx.description}</p>
                      {tx.payment_reference && <p className="text-xs text-gray-500 mt-1">Ref: {tx.payment_reference}</p>}
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <p>{new Date(tx.created_at).toLocaleDateString('es-VE', { timeZone: 'America/Caracas', day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                      <p>{new Date(tx.created_at).toLocaleTimeString('es-VE', { timeZone: 'America/Caracas', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-6 border-t"><button onClick={onClose} className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50">Cerrar</button></div>
      </div>
    </div>
  );
}


function CommissionLiquidationPanel({ setToast }) {
  const [eligible, setEligible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferModal, setTransferModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/commission-liquidation/eligible-merchants`, { headers });
      setEligible(res.data);
    } catch (e) {
      setToast?.({ type: 'error', message: 'Error cargando merchants' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="bg-white rounded-xl border p-5 mb-6" data-testid="commission-liquidation-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: '#051C2C' }}>
            <DollarSign className="w-5 h-5 text-orange-600" /> Comisiones por comercio (transferencia manual)
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            La dispersión automática está deshabilitada. Para barrer comisiones de un comercio,
            haz click en "Transferir" y completa los datos del destino. Genera un CCE inmediato
            desde la cuenta concentradora del merchant a la cuenta destino.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="liquidation-refresh"
          disabled={loading}
          className="text-xs px-3 py-1.5 border rounded-lg hover:bg-slate-50"
        >
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2 text-left">Comercio</th>
              <th className="px-3 py-2 text-left">RIF</th>
              <th className="px-3 py-2 text-left">Cuenta concentradora</th>
              <th className="px-3 py-2 text-right">Balance commission</th>
              <th className="px-3 py-2 text-right">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(eligible?.merchants || []).length === 0 && !loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">Sin merchants con concentradora propia.</td></tr>
            )}
            {(eligible?.merchants || []).map(m => (
              <tr key={m.merchant_id} className="hover:bg-slate-50" data-testid={`liquidation-row-${m.merchant_id}`}>
                <td className="px-3 py-2 text-sm">{m.company_name} <div className="text-[10px] text-slate-500">{m.email}</div></td>
                <td className="px-3 py-2 font-mono text-xs">{m.rif}</td>
                <td className="px-3 py-2 font-mono text-xs">{m.cuenta_operativa} <span className="text-slate-400">({m.cuenta_bank_code})</span></td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${m.commission_balance_ves > 0 ? 'text-orange-700' : 'text-slate-400'}`}>
                  VES {m.commission_balance_ves.toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setTransferModal(m)}
                    disabled={m.commission_balance_ves < 0.01}
                    data-testid={`transfer-merchant-${m.merchant_id}`}
                    className="text-xs px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={m.commission_balance_ves < 0.01 ? 'Sin comisiones a transferir' : 'Abrir modal de transferencia'}
                  >
                    Transferir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {transferModal && (
        <ManualTransferModal
          merchant={transferModal}
          onClose={() => setTransferModal(null)}
          onSuccess={() => { setTransferModal(null); load(); }}
          setToast={setToast}
        />
      )}
    </div>
  );
}


function ManualTransferModal({ merchant, onClose, onSuccess, setToast }) {
  const [form, setForm] = useState({
    source_account: merchant.cuenta_operativa,
    source_rif: merchant.rif,
    source_bank_code: merchant.cuenta_bank_code || '0138',
    source_name: merchant.titular_name || merchant.company_name,
    dest_account: '01380010320100324207',  // Caura por default
    dest_rif: 'J000000408320126',
    dest_bank_code: '0138',
    dest_name: 'Caura Pay',
    dest_doc_type: 'J',
    amount: merchant.commission_balance_ves.toFixed(2),
    concepto: `Liquidación comisiones ${merchant.company_name}`,
    debit_wallet_id: merchant.wallet_id,
  });
  const [busy, setBusy] = useState(false);
  const [saveBenef, setSaveBenef] = useState(false);
  const [benefAlias, setBenefAlias] = useState('');
  const [savedList, setSavedList] = useState([]);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const loadSaved = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/saved-beneficiaries`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedList(res.data || []);
    } catch (err) {
      // Auxiliary feature; log but don't block the modal
      console.warn('[saved-beneficiaries] failed to load:', err?.response?.data?.detail || err?.message || err);
    }
  };

  useEffect(() => { loadSaved(); }, []);

  const filteredSaved = (() => {
    const term = search.trim().toLowerCase();
    if (!term) return savedList;
    return savedList.filter(b =>
      (b.alias || '').toLowerCase().includes(term) ||
      (b.account_number || '').toLowerCase().includes(term) ||
      (b.holder_id || '').toLowerCase().includes(term) ||
      (b.holder_name || '').toLowerCase().includes(term)
    );
  })();

  const applyBeneficiary = (b) => {
    setForm(f => ({
      ...f,
      dest_account: b.account_number,
      dest_rif: b.holder_id,
      dest_bank_code: b.bank_code,
      dest_name: b.holder_name,
      dest_doc_type: b.holder_doc_type || 'J',
    }));
    setSearch(b.alias);
    setShowDropdown(false);
    setSaveBenef(false);
    setBenefAlias('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Confirmar transferencia de VES ${form.amount} desde ${merchant.company_name} a ${form.dest_name}?`)) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (saveBenef) {
        payload.save_beneficiary = true;
        if (benefAlias.trim()) payload.beneficiary_alias = benefAlias.trim();
      }
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/transfers/manual`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.ok) {
        setToast?.({
          type: 'success',
          message: `Transferencia ejecutada · VES ${res.data.amount} · ref ${res.data.bank_reference || 'pendiente'}`,
        });
        onSuccess?.();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.error || JSON.stringify(detail)) : (detail || err.message);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setBusy(false);
    }
  };

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="manual-transfer-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: '#051C2C' }}>
          <div className="text-white">
            <h3 className="text-lg font-bold">Transferencia manual (CCE)</h3>
            <p className="text-xs text-blue-200">{merchant.company_name} · Balance disponible: VES {merchant.commission_balance_ves.toFixed(2)}</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-300 text-xl leading-none" data-testid="manual-transfer-close">✕</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <h4 className="text-xs font-bold uppercase text-slate-500 mb-2 tracking-wide">Origen (cuenta del merchant)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-500">Cuenta (20 dígitos)</label>
                <input value={form.source_account} onChange={update('source_account')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={20} required />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">RIF titular</label>
                <input value={form.source_rif} onChange={update('source_rif')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" required />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Banco origen (4 dígitos)</label>
                <input value={form.source_bank_code} onChange={update('source_bank_code')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={4} required />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Titular origen</label>
                <input value={form.source_name} onChange={update('source_name')} className="w-full px-3 py-1.5 border rounded text-sm" required />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase text-slate-500 tracking-wide">Destino</h4>
              <button
                type="button"
                onClick={() => setManageOpen(true)}
                className="text-[11px] text-blue-700 hover:underline"
                data-testid="manage-beneficiaries-btn"
              >
                Gestionar cuentas guardadas ({savedList.length})
              </button>
            </div>

            {/* Selector de cuenta guardada (autocomplete) */}
            <div className="relative mb-3">
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                placeholder="🔍 Buscar cuenta guardada por alias, cuenta, RIF o titular…"
                data-testid="saved-beneficiary-search"
                className="w-full px-3 py-2 border rounded text-sm bg-slate-50 focus:bg-white"
              />
              {showDropdown && filteredSaved.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="saved-beneficiary-dropdown">
                  {filteredSaved.slice(0, 30).map(b => (
                    <button
                      type="button"
                      key={b.beneficiary_id}
                      onMouseDown={(e) => { e.preventDefault(); applyBeneficiary(b); }}
                      data-testid={`saved-beneficiary-option-${b.beneficiary_id}`}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0"
                    >
                      <div className="text-sm font-medium" style={{ color: '#051C2C' }}>{b.alias}</div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {b.account_number} · {b.bank_code} · {b.holder_doc_type}{b.holder_id} · {b.holder_name}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showDropdown && search && filteredSaved.length === 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg p-3 text-xs text-slate-500">
                  Sin coincidencias. Completa los campos abajo y marca "Guardar cuenta" para añadirla.
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-500">Cuenta (20 dígitos)</label>
                <input value={form.dest_account} onChange={update('dest_account')} data-testid="transfer-dest-account" className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={20} required />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">RIF/Cédula titular (con letra V/E/J/G)</label>
                <input value={form.dest_rif} onChange={update('dest_rif')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="V12345678 ó J000000408320126" required />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Banco destino (4 dígitos)</label>
                <input value={form.dest_bank_code} onChange={update('dest_bank_code')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={4} required />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Tipo doc. destino</label>
                <select value={form.dest_doc_type} onChange={update('dest_doc_type')} className="w-full px-3 py-1.5 border rounded text-sm">
                  <option value="J">J (Jurídico)</option>
                  <option value="V">V (Venezolano)</option>
                  <option value="E">E (Extranjero)</option>
                  <option value="G">G (Gobierno)</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-slate-500">Titular destino</label>
                <input value={form.dest_name} onChange={update('dest_name')} className="w-full px-3 py-1.5 border rounded text-sm" required />
              </div>
            </div>

            {/* Guardar cuenta */}
            <div className="mt-3 rounded-lg border bg-slate-50 p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveBenef}
                  onChange={(e) => setSaveBenef(e.target.checked)}
                  data-testid="save-beneficiary-checkbox"
                  className="rounded"
                />
                <span className="text-sm font-medium text-slate-700">Guardar cuenta para futuros usos</span>
              </label>
              {saveBenef && (
                <div className="mt-2">
                  <label className="text-[10px] text-slate-500">Alias (etiqueta personalizada)</label>
                  <input
                    value={benefAlias}
                    onChange={(e) => setBenefAlias(e.target.value)}
                    placeholder={`p.ej. "Proveedor ${form.dest_name}"`}
                    data-testid="save-beneficiary-alias"
                    maxLength={80}
                    className="w-full px-3 py-1.5 border rounded text-sm"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">Si dejas vacío se usará el nombre del titular.</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500">Monto VES</label>
              <input
                type="number" step="0.01" min="0.01"
                value={form.amount}
                onChange={update('amount')}
                data-testid="transfer-amount"
                className="w-full px-3 py-1.5 border rounded text-sm font-mono font-bold"
                required
              />
              <p className="text-[10px] text-slate-400 mt-0.5">Disponible: VES {merchant.commission_balance_ves.toFixed(2)}</p>
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Concepto</label>
              <input value={form.concepto} onChange={update('concepto')} className="w-full px-3 py-1.5 border rounded text-sm" maxLength={80} />
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <strong>⚠️ Pre-condiciones:</strong> la cuenta origen debe tener firmada la <strong>autorización de cargo</strong> en Banco Plaza para que nuestro RIF (J000000408320126) inicie débitos. Si Banco Plaza responde código <code>A003</code>, la cuenta no está autorizada y debes coordinar con el merchant para que firme el formulario en su sucursal.
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-50">Cancelar</button>
            <button
              type="submit"
              disabled={busy}
              data-testid="transfer-submit"
              className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {busy ? 'Ejecutando…' : 'Ejecutar transferencia'}
            </button>
          </div>
        </form>
      </div>

      {manageOpen && (
        <SavedBeneficiariesManager
          beneficiaries={savedList}
          onClose={() => setManageOpen(false)}
          onChanged={loadSaved}
          setToast={setToast}
        />
      )}
    </div>
  );
}


function SavedBeneficiariesManager({ beneficiaries, onClose, onChanged, setToast }) {
  const [editing, setEditing] = useState(null); // {id, alias, holder_name, bank_code, bank_name}
  const [busyId, setBusyId] = useState(null);

  const startEdit = (b) => setEditing({
    beneficiary_id: b.beneficiary_id,
    alias: b.alias || '',
    holder_name: b.holder_name || '',
    bank_code: b.bank_code || '',
    bank_name: b.bank_name || '',
    notes: b.notes || '',
  });

  const saveEdit = async () => {
    if (!editing) return;
    setBusyId(editing.beneficiary_id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/super-admin/saved-beneficiaries/${editing.beneficiary_id}`,
        {
          alias: editing.alias,
          holder_name: editing.holder_name,
          bank_code: editing.bank_code,
          bank_name: editing.bank_name,
          notes: editing.notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToast?.({ type: 'success', message: 'Cuenta actualizada' });
      setEditing(null);
      onChanged?.();
    } catch (err) {
      setToast?.({ type: 'error', message: err.response?.data?.detail || 'Error al actualizar' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`¿Eliminar la cuenta guardada "${b.alias}"? Esto no afecta transferencias pasadas.`)) return;
    setBusyId(b.beneficiary_id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/super-admin/saved-beneficiaries/${b.beneficiary_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setToast?.({ type: 'success', message: 'Cuenta eliminada' });
      onChanged?.();
    } catch (err) {
      setToast?.({ type: 'error', message: err.response?.data?.detail || 'Error al eliminar' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="manage-saved-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: '#003594' }}>
          <h3 className="text-white font-bold">Cuentas destino guardadas</h3>
          <button onClick={onClose} className="text-white hover:text-gray-300 text-xl leading-none" data-testid="manage-saved-close">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {beneficiaries.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-12">
              Aún no hay cuentas guardadas. Marca "Guardar cuenta para futuros usos" al ejecutar una transferencia.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Alias</th>
                  <th className="px-3 py-2 text-left">Cuenta</th>
                  <th className="px-3 py-2 text-left">Titular</th>
                  <th className="px-3 py-2 text-right">Usos</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {beneficiaries.map(b => (
                  <tr key={b.beneficiary_id} data-testid={`saved-row-${b.beneficiary_id}`}>
                    <td className="px-3 py-2 font-medium" style={{ color: '#051C2C' }}>{b.alias}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      <div>{b.account_number}</div>
                      <div className="text-slate-500">{b.bank_code} · {b.bank_name || ''}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>{b.holder_name}</div>
                      <div className="text-slate-500 font-mono">{b.holder_doc_type}{b.holder_id}</div>
                    </td>
                    <td className="px-3 py-2 text-right text-xs">{b.use_count || 0}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => startEdit(b)}
                        disabled={busyId === b.beneficiary_id}
                        data-testid={`edit-saved-${b.beneficiary_id}`}
                        className="text-xs px-2 py-1 border rounded hover:bg-slate-50 mr-1"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => remove(b)}
                        disabled={busyId === b.beneficiary_id}
                        data-testid={`delete-saved-${b.beneficiary_id}`}
                        className="text-xs px-2 py-1 border border-red-200 text-red-700 rounded hover:bg-red-50"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {editing && (
          <div className="border-t bg-slate-50 p-4">
            <h4 className="text-sm font-bold mb-2" style={{ color: '#051C2C' }}>Editar cuenta guardada</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] text-slate-500">Alias</label>
                <input
                  value={editing.alias}
                  onChange={(e) => setEditing(s => ({ ...s, alias: e.target.value }))}
                  data-testid="edit-saved-alias"
                  className="w-full px-3 py-1.5 border rounded text-sm"
                  maxLength={80}
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Titular</label>
                <input value={editing.holder_name} onChange={(e) => setEditing(s => ({ ...s, holder_name: e.target.value }))} className="w-full px-3 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Banco (4 dígitos)</label>
                <input value={editing.bank_code} onChange={(e) => setEditing(s => ({ ...s, bank_code: e.target.value }))} maxLength={4} className="w-full px-3 py-1.5 border rounded text-sm font-mono" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-slate-500">Banco (nombre)</label>
                <input value={editing.bank_name} onChange={(e) => setEditing(s => ({ ...s, bank_name: e.target.value }))} className="w-full px-3 py-1.5 border rounded text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-slate-500">Notas</label>
                <input value={editing.notes} onChange={(e) => setEditing(s => ({ ...s, notes: e.target.value }))} className="w-full px-3 py-1.5 border rounded text-sm" maxLength={500} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm border rounded">Cancelar</button>
              <button
                onClick={saveEdit}
                disabled={busyId === editing.beneficiary_id}
                data-testid="save-edit-saved"
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Guardar cambios
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
